import {
  createDarkTheme,
  createLightTheme,
  teamsHighContrastTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

/**
 * Theme identifiers reported by `@microsoft/teams-js`.
 * Teams uses `default | dark | contrast`; older clients may send `teamsHighContrast`.
 */
export type TeamsThemeName = 'default' | 'dark' | 'contrast';

/** Value written to the `data-theme` attribute consumed by tokens.css. */
export type DocumentThemeName = 'light' | 'dark' | 'contrast';

const TEAMS_THEMES: readonly TeamsThemeName[] = ['default', 'dark', 'contrast'];

/** Normalizes any string the Teams SDK (or a stored preference) may provide. */
export function toTeamsTheme(value: string | null | undefined): TeamsThemeName {
  if (!value) return 'default';
  const normalized = value.toLowerCase();
  if (normalized === 'light') return 'default';
  if (normalized.includes('contrast')) return 'contrast';
  if (normalized === 'dark') return 'dark';
  return TEAMS_THEMES.includes(normalized as TeamsThemeName)
    ? (normalized as TeamsThemeName)
    : 'default';
}


/**
 * ADCO brand ramp for Fluent.
 *
 * Without it Fluent paints every primary control in its stock Microsoft blue,
 * which is both off-brand and short of WCAG 1.4.3: white text on `#1e73bd`
 * measures 4.38:1 at 14px, under the 4.5:1 the spec requires. The ADCO navy is
 * far darker, so the same button clears it comfortably.
 *
 * The 16 stops mirror the `--color-brand-*` scale in styles/tokens.css, with
 * the ends extrapolated: Fluent needs shades our Tailwind scale does not carry.
 */
const adcoBrand: BrandVariants = {
  10: '#050810',
  20: '#0b1220',
  30: '#0f1a30',
  40: '#152442',
  50: '#182748',
  60: '#1e325d',
  70: '#22375f',
  80: '#253d72',
  90: '#2c477f',
  100: '#35528d',
  110: '#4869ab',
  120: '#6d8bc6',
  130: '#9db2dc',
  140: '#c6d3ec',
  150: '#e3eaf6',
  160: '#f2f5fb',
};

const adcoLightTheme: Theme = createLightTheme(adcoBrand);
const adcoDarkTheme: Theme = createDarkTheme(adcoBrand);

/** Maps the Teams theme to the matching Fluent UI v9 theme. */
export function fluentThemeFor(theme: TeamsThemeName): Theme {
  switch (theme) {
    case 'dark':
      return adcoDarkTheme;
    case 'contrast':
      return teamsHighContrastTheme;
    default:
      return adcoLightTheme;
  }
}

/** Maps the Teams theme to the `data-theme` attribute used by our tokens. */
export function documentThemeFor(theme: TeamsThemeName): DocumentThemeName {
  switch (theme) {
    case 'dark':
      return 'dark';
    case 'contrast':
      return 'contrast';
    default:
      return 'light';
  }
}

export function documentThemeToTeamsTheme(theme: DocumentThemeName): TeamsThemeName {
  return theme === 'light' ? 'default' : theme;
}

/** Applies the theme to <html> so plain CSS and Tailwind classes follow along. */
export function applyDocumentTheme(theme: TeamsThemeName): void {
  if (typeof document === 'undefined') return;
  const value = documentThemeFor(theme);
  document.documentElement.setAttribute('data-theme', value);
  document.documentElement.style.colorScheme = value === 'light' ? 'light' : 'dark';
}

/** Detects the preferred theme when running outside Teams. */
export function detectBrowserTheme(): TeamsThemeName {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'default';
  if (window.matchMedia('(forced-colors: active)').matches) return 'contrast';
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'default';
}

/** Persisted only for the browser fallback; contains no sensitive data. */
export const THEME_STORAGE_KEY = 'adco-rooms:theme';

export function readStoredTheme(): TeamsThemeName | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'contrast') {
      return toTeamsTheme(stored);
    }
  } catch {
    // Storage can be blocked inside the Teams iframe; ignore silently.
  }
  return null;
}

export function storeTheme(theme: TeamsThemeName): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, documentThemeFor(theme));
  } catch {
    // Non fatal: the theme still applies for the current session.
  }
}
